import { browserHistory } from 'src/AppRoutes';
import { PAGE_PATHS } from 'src/constants/Urls';
import { ErrorCode } from 'src/constants/enums';
import { ExceptionDetail } from 'src/models/ExceptionDetail';
import { UrlUtils } from 'src/utils/UrlUtils';

/**
 * Engineer-facing context captured alongside a failure so SDEs can investigate
 * without going back to CloudWatch. Rendered in the collapsible "Technical
 * details" panel on the failure page.
 */
export interface FailureContext {
  /** The parsed API exception, if the failure came from a service call. */
  exception?: ExceptionDetail;
  /** Human name of the page / operation that failed, e.g. 'Users - getUsersInDomain'. */
  source?: string;
  /** API endpoint that failed, if known. */
  api?: string;
  /** Katal sessionId so logs can be correlated. */
  sessionId?: string;
  /** ISO timestamp of when the failure was captured. */
  timestamp?: string;
  /** React component stack for uncaught render errors (from the error boundary). */
  componentStack?: string;
}

/**
 * Module-level holder for the most recent failure context.
 *
 * The failure page lives on a separate route (`/error/:errorCode`) and the
 * page that failed unmounts before it renders, so the caught error object
 * cannot be passed through React props. `history.push` state is also lost on a
 * hard reload. We stash the context here and the failure page reads it on
 * mount. It is deliberately a single most-recent slot (not a stack) because
 * only the latest failure is being displayed.
 */
let lastFailureContext: FailureContext | undefined;

export const setLastFailureContext = (context: FailureContext): void => {
  lastFailureContext = context;
};

export const consumeLastFailureContext = (): FailureContext | undefined => {
  const context = lastFailureContext;
  // Consume (clear) on read so a subsequent direct/bookmarked visit to
  // /error/:errorCode — or a route-guard redirect that carries no context —
  // does not render a previous, unrelated failure's engineer details.
  lastFailureContext = undefined;
  return context;
};

/**
 * Derive the `:errorCode` route segment from a parsed exception. We route 4xx
 * by their concrete status (so the page can message 403 vs 404 distinctly) and
 * everything else through the generic-failure bucket.
 */
const resolveErrorRouteCode = (error?: ExceptionDetail): string => {
  const status = error?.errorStatus;
  if (
    status !== undefined &&
    status !== null &&
    /^4\d{2}$/.test(status.toString())
  ) {
    return status.toString();
  }
  if (
    status !== undefined &&
    status !== null &&
    /^5\d{2}$/.test(status.toString())
  ) {
    return status.toString();
  }
  // No HTTP status but Axios flagged a transport failure (no response reached
  // the browser: network drop, CORS, unreachable, or timeout). Route to the
  // connectivity page so the user gets an actionable message instead of the
  // opaque generic bucket.
  if (error?.transportCode) {
    return ErrorCode.CONNECTION_ERROR;
  }
  return ErrorCode.GENERIC_FAILURE;
};

/**
 * Centralized replacement for the repeated
 *   `if (4xx) push(FAILURE_PAGE, errorStatus) else push(FAILURE_PAGE, GENERIC_FAILURE)`
 * blocks. Captures full engineer context, then navigates to the failure page.
 *
 * @param error   the caught exception (parsed ExceptionDetail preferred).
 * @param context optional source/api/sessionId metadata for the SDE panel.
 * @param replace use history.replace instead of push (for error boundaries).
 */
export const redirectToFailurePage = (
  error?: ExceptionDetail,
  context: Omit<FailureContext, 'exception' | 'timestamp'> = {},
  replace = false
): void => {
  setLastFailureContext({
    exception: error,
    timestamp: new Date().toISOString(),
    ...context
  });

  const routeCode = resolveErrorRouteCode(error);
  const url = UrlUtils.prepareUrl(PAGE_PATHS.FAILURE_PAGE, routeCode);
  // Pages often fire several fetches concurrently; if more than one fails, each
  // catch would push its own /error entry and Back would cycle through
  // duplicates. If we are already on the failure page, replace instead of push.
  const alreadyOnFailurePage = browserHistory.location.pathname.startsWith(
    PAGE_PATHS.FAILURE_PAGE.replace(':errorCode', '')
  );
  if (replace || alreadyOnFailurePage) {
    browserHistory.replace(url);
  } else {
    browserHistory.push(url);
  }
};
